<template>
  <div class="container-fluid">
    <!-- Header -->
    <div class="row align-items-center mb-3 d-print-none">
      <div class="col-md-5 col-12">
        <div class="pageTitle">Compliance & Performance Reports</div>
        <p class="text-muted mb-0">Track agent resolution efficiency, SLA compliance standards, and student repeat contact patterns.</p>
      </div>
      <div class="col-md-7 col-12 text-md-right mt-3 mt-md-0">
        <span class="badge badge-primary px-3 py-2 mr-2" style="font-size: 13px;">
          <b-icon-calendar-range class="mr-1"></b-icon-calendar-range> {{ periodLabel }}
        </span>
        <b-button variant="success" size="sm" @click="exportCSV" class="mr-2">
          <b-icon-file-earmark-spreadsheet class="mr-1"></b-icon-file-earmark-spreadsheet> Export to CSV
        </b-button>
        <b-button variant="danger" size="sm" @click="exportPDF" class="mr-2">
          <b-icon-file-earmark-pdf class="mr-1"></b-icon-file-earmark-pdf> Export to PDF
        </b-button>
        <b-button variant="outline-primary" size="sm" @click="fetchReports(currentPeriod)">
          <b-icon-arrow-clockwise></b-icon-arrow-clockwise> Refresh
        </b-button>
      </div>
    </div>

    <!-- Official University Print Banner (Visible only in PDF / Print) -->
    <div class="d-none d-print-block mb-4 text-center">
      <h3 class="font-weight-bold mb-1" style="color: #0061f2;">National Open University of Nigeria</h3>
      <h5 class="text-dark font-weight-bold mb-1">Support Ticketing System — Compliance & Performance Reports</h5>
      <p class="text-muted small mb-0">
        Reporting Period: <strong>{{ periodLabel }}</strong> |
        SLA Standard: <strong>{{ ackSummary.standardHours }} Hours</strong> |
        Resolution SLA Standard: <strong>{{ resSummary.standardHours }} Hours</strong> |
        Generated: {{ new Date().toLocaleString() }}
      </p>
      <hr class="my-3" style="border-top: 2px solid #0061f2;" />
    </div>

    <!-- Filter Control Bar -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card shadow-sm border-0 filter-card">
          <div class="card-body py-3">
            <div class="row align-items-center">
              <!-- Period Selector -->
              <div class="col-lg-3 col-md-6 col-12 mb-2 mb-lg-0">
                <label class="font-weight-bold text-muted small text-uppercase mb-1 d-block">Reporting Period</label>
                <b-form-select v-model="currentPeriod" @change="onPeriodChange" class="form-control-sm">
                  <option value="month">This Month</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="year">This Year</option>
                  <option value="range">Custom Date Range</option>
                </b-form-select>
              </div>

              <!-- Date Range Picker (shown when range selected) -->
              <div class="col-lg-3 col-md-6 col-12 mb-2 mb-lg-0" v-show="currentPeriod === 'range'">
                <label class="font-weight-bold text-muted small text-uppercase mb-1 d-block">Select Range</label>
                <RangedatePicker @selected="onDateRangeSelected" i18n="EN" class="myDateRangePicker" righttoleft="true" />
              </div>

              <!-- Ack SLA Standard -->
              <div class="col-lg-3 col-md-6 col-12 mb-2 mb-lg-0">
                <label class="font-weight-bold text-muted small text-uppercase mb-1 d-block">
                  Service Standard
                </label>
                <b-form-select v-model="ackSlaHours" @change="fetchReports(currentPeriod)" class="form-control-sm">
                  <option :value="2">Within 2 Hours (Urgent)</option>
                  <option :value="4">Within 4 Hours</option>
                  <option :value="12">Within 12 Hours</option>
                  <option :value="24">Within 24 Hours (Standard)</option>
                  <option :value="48">Within 48 Hours</option>
                </b-form-select>
              </div>

              <!-- Resolution SLA Standard -->
              <div class="col-lg-3 col-md-6 col-12 mb-2 mb-lg-0">
                <label class="font-weight-bold text-muted small text-uppercase mb-1 d-block">
                  Resolution Timeline SLA
                </label>
                <b-form-select v-model="resSlaHours" @change="fetchReports(currentPeriod)" class="form-control-sm">
                  <option :value="24">Within 24 Hours</option>
                  <option :value="48">Within 48 Hours (Standard)</option>
                  <option :value="72">Within 72 Hours (Academic)</option>
                  <option :value="96">Within 96 Hours</option>
                </b-form-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 4 Core Executive KPI Cards -->
    <div class="row">
      <!-- 1. Agent Resolution Rate -->
      <div class="col-xl-3 col-lg-6 col-md-6 col-12 mb-4">
        <div class="card h-100 kpi-card border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="kpi-title text-muted text-uppercase small font-weight-bold">1. Agent Resolution Rate</div>
                <div class="kpi-value text-success font-weight-bold">{{ agentResSummary.resolutionRate }}%</div>
              </div>
              <div class="kpi-icon-wrapper bg-soft-success">
                <b-icon-person-check-fill class="kpi-icon text-success"></b-icon-person-check-fill>
              </div>
            </div>
            <p class="kpi-subtext text-muted small mb-3">
              Enquiries resolved by first-contact agents without escalation
            </p>
            <b-progress :value="agentResSummary.resolutionRate" variant="success" max="100" height="6px" class="mb-3"></b-progress>
            <div class="kpi-breakdown small">
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">First-Contact Resolved:</span>
                <span class="font-weight-bold text-success">{{ agentResSummary.firstContactResolved | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">Total Resolved Enquiries:</span>
                <span class="font-weight-bold">{{ agentResSummary.totalClosed | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1">
                <span class="text-muted">Escalated Enquiries:</span>
                <span class="font-weight-bold text-warning">{{ agentResSummary.escalatedEnquiries | thousandSeparator }}</span>
              </div>
            </div>
          </div>
          <div class="card-footer bg-light border-0 py-2">
            <small class="text-muted">Overall FCR: <strong>{{ agentResSummary.overallFcrRate }}%</strong> of all enquiries</small>
          </div>
        </div>
      </div>

      <!-- 2. Ticket Acknowledgement Compliance -->
      <div class="col-xl-3 col-lg-6 col-md-6 col-12 mb-4">
        <div class="card h-100 kpi-card border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="kpi-title text-muted text-uppercase small font-weight-bold">2. Acknowledgement Compliance</div>
                <div class="kpi-value text-info font-weight-bold">{{ ackSummary.complianceRate }}%</div>
              </div>
              <div class="kpi-icon-wrapper bg-soft-info">
                <b-icon-clock-history class="kpi-icon text-info"></b-icon-clock-history>
              </div>
            </div>
            <p class="kpi-subtext text-muted small mb-3">
              Tickets acknowledged within approved standard (<strong>{{ ackSummary.standardHours }}h</strong>)
            </p>
            <b-progress :value="ackSummary.complianceRate" variant="info" max="100" height="6px" class="mb-3"></b-progress>
            <div class="kpi-breakdown small">
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">Acknowledged in Standard:</span>
                <span class="font-weight-bold text-info">{{ ackSummary.compliantCount | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">SLA Breached Acknowledgement:</span>
                <span class="font-weight-bold text-danger">{{ ackSummary.breachedCount | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1">
                <span class="text-muted">Pending First Response:</span>
                <span class="font-weight-bold text-warning">{{ ackSummary.pendingAck | thousandSeparator }}</span>
              </div>
            </div>
          </div>
          <div class="card-footer bg-light border-0 py-2">
            <small class="text-muted">Avg time to acknowledge: <strong>{{ ackSummary.avgAckHours }} hrs</strong></small>
          </div>
        </div>
      </div>

      <!-- 3. Ticket Resolution Compliance -->
      <div class="col-xl-3 col-lg-6 col-md-6 col-12 mb-4">
        <div class="card h-100 kpi-card border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="kpi-title text-muted text-uppercase small font-weight-bold">3. Resolution Compliance</div>
                <div class="kpi-value text-primary font-weight-bold">{{ resSummary.complianceRate }}%</div>
              </div>
              <div class="kpi-icon-wrapper bg-soft-primary">
                <b-icon-check2-circle class="kpi-icon text-primary"></b-icon-check2-circle>
              </div>
            </div>
            <p class="kpi-subtext text-muted small mb-3">
              Tickets resolved within category SLA (<strong>{{ resSummary.standardHours }}h</strong> timeline)
            </p>
            <b-progress :value="resSummary.complianceRate" variant="primary" max="100" height="6px" class="mb-3"></b-progress>
            <div class="kpi-breakdown small">
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">Resolved Within Timeline:</span>
                <span class="font-weight-bold text-primary">{{ resSummary.compliantResolved | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">Resolution Timeline Breached:</span>
                <span class="font-weight-bold text-danger">{{ resSummary.breachedResolved | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1">
                <span class="text-muted">Currently Overdue Open:</span>
                <span class="font-weight-bold text-danger">{{ resSummary.overdueUnresolved | thousandSeparator }}</span>
              </div>
            </div>
          </div>
          <div class="card-footer bg-light border-0 py-2">
            <small class="text-muted">Avg time to resolve: <strong>{{ resSummary.avgResolutionHours }} hrs</strong></small>
          </div>
        </div>
      </div>

      <!-- 4. Repeat Contact Rate -->
      <div class="col-xl-3 col-lg-6 col-md-6 col-12 mb-4">
        <div class="card h-100 kpi-card border-0 shadow-sm">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="kpi-title text-muted text-uppercase small font-weight-bold">4. Repeat Contact Rate</div>
                <div :class="['kpi-value font-weight-bold', repeatSummary.repeatContactRate > 20 ? 'text-danger' : 'text-warning']">
                  {{ repeatSummary.repeatContactRate }}%
                </div>
              </div>
              <div class="kpi-icon-wrapper bg-soft-warning">
                <b-icon-arrow-repeat class="kpi-icon text-warning"></b-icon-arrow-repeat>
              </div>
            </div>
            <p class="kpi-subtext text-muted small mb-3">
              Students contacting more than once regarding the same unresolved issue
            </p>
            <b-progress :value="repeatSummary.repeatContactRate" :variant="repeatSummary.repeatContactRate > 20 ? 'danger' : 'warning'" max="100" height="6px" class="mb-3"></b-progress>
            <div class="kpi-breakdown small">
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">Repeat Inquiring Students:</span>
                <span class="font-weight-bold text-warning">{{ repeatSummary.repeatStudents | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span class="text-muted">Single-Contact Students:</span>
                <span class="font-weight-bold text-success">{{ repeatSummary.singleContactStudents | thousandSeparator }}</span>
              </div>
              <div class="d-flex justify-content-between py-1">
                <span class="text-muted">Reopened Tickets Count:</span>
                <span class="font-weight-bold text-danger">{{ repeatSummary.totalReopenedTickets | thousandSeparator }}</span>
              </div>
            </div>
          </div>
          <div class="card-footer bg-light border-0 py-2">
            <small class="text-muted">Total Active Students: <strong>{{ repeatSummary.totalInquiringStudents | thousandSeparator }}</strong></small>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row mb-4">
      <!-- Trends Chart -->
      <div class="col-lg-8 col-12 mb-4 mb-lg-0">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-header bg-white text-dark font-weight-bold py-3 chart-card-header">
            <b-icon-graph-up class="mr-2 text-primary"></b-icon-graph-up>
            Compliance & Resolution Rates Trend
          </div>
          <div class="card-body">
            <highcharts :options="trendsChartOptions"></highcharts>
          </div>
        </div>
      </div>

      <!-- Channel Acknowledgement Distribution Chart -->
      <div class="col-lg-4 col-12">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-header bg-white text-dark font-weight-bold py-3 chart-card-header">
            <b-icon-diagram-3 class="mr-2 text-info"></b-icon-diagram-3>
            Contact Channels Breakdown
          </div>
          <div class="card-body">
            <highcharts :options="channelChartOptions"></highcharts>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Tabbed Tables Section -->
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm border-0 mb-5">
          <div class="card-header bg-white border-bottom-0 pb-0 d-print-none">
            <b-nav tabs class="report-tabs">
              <b-nav-item :active="activeTab === 'agents'" @click="activeTab = 'agents'">
                <b-icon-person-check class="mr-1"></b-icon-person-check> Agent Resolution Details
              </b-nav-item>
              <b-nav-item :active="activeTab === 'ack'" @click="activeTab = 'ack'">
                <b-icon-clock-history class="mr-1"></b-icon-clock-history> Acknowledgement Compliance (Categories)
              </b-nav-item>
              <b-nav-item :active="activeTab === 'res'" @click="activeTab = 'res'">
                <b-icon-check2-circle class="mr-1"></b-icon-check2-circle> Resolution Compliance (Categories)
              </b-nav-item>
              <b-nav-item :active="activeTab === 'repeat'" @click="activeTab = 'repeat'">
                <b-icon-arrow-repeat class="mr-1"></b-icon-arrow-repeat> Repeat Contact Issues
              </b-nav-item>
            </b-nav>
          </div>

          <div class="card-body pt-3">
            <!-- Tab 1: Agent Resolution -->
            <div :class="['tab-report-section mb-4', activeTab === 'agents' ? '' : 'd-none d-print-block']">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold mb-0 text-dark">1. First-Contact Resolution by Agent</h6>
                <small class="text-muted">Shows how each agent resolves inquiries without forwarding or escalating</small>
              </div>
              <vue-good-table
                :columns="agentColumns"
                :rows="agentBreakdown"
                :line-numbers="true"
                :search-options="{ enabled: true, placeholder: 'Search agent name or email...' }"
                :pagination-options="{ enabled: true, perPage: 10 }"
              >
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'resolutionRate'">
                    <span :class="['badge px-2 py-1', props.row.resolutionRate >= 70 ? 'badge-success' : 'badge-warning']" style="font-size: 12px;">
                      {{ props.row.resolutionRate }}%
                    </span>
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>

            <!-- Tab 2: Acknowledgement Compliance -->
            <div :class="['tab-report-section mb-4', activeTab === 'ack' ? '' : 'd-none d-print-block']">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold mb-0 text-dark">2. Ticket Acknowledgement Compliance by Category</h6>
                <small class="text-muted">Approved Service Standard: {{ ackSummary.standardHours }} Hours</small>
              </div>
              <vue-good-table
                :columns="ackCatColumns"
                :rows="ackCategoryBreakdown"
                :line-numbers="true"
                :search-options="{ enabled: true, placeholder: 'Search category...' }"
                :pagination-options="{ enabled: true, perPage: 10 }"
              >
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'complianceRate'">
                    <span :class="['badge px-2 py-1', props.row.complianceRate >= 80 ? 'badge-success' : (props.row.complianceRate >= 50 ? 'badge-warning' : 'badge-danger')]" style="font-size: 12px;">
                      {{ props.row.complianceRate }}%
                    </span>
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>

            <!-- Tab 3: Resolution Compliance -->
            <div :class="['tab-report-section mb-4', activeTab === 'res' ? '' : 'd-none d-print-block']">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold mb-0 text-dark">3. Resolution Compliance by Issue Category</h6>
                <small class="text-muted">Target Resolution Timeline: {{ resSummary.standardHours }} Hours</small>
              </div>
              <vue-good-table
                :columns="resCatColumns"
                :rows="resCategoryBreakdown"
                :line-numbers="true"
                :search-options="{ enabled: true, placeholder: 'Search category...' }"
                :pagination-options="{ enabled: true, perPage: 10 }"
              >
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'complianceRate'">
                    <span :class="['badge px-2 py-1', props.row.complianceRate >= 75 ? 'badge-success' : (props.row.complianceRate >= 50 ? 'badge-warning' : 'badge-danger')]" style="font-size: 12px;">
                      {{ props.row.complianceRate }}%
                    </span>
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>

            <!-- Tab 4: Repeat Contact Analysis -->
            <div :class="['tab-report-section mb-4', activeTab === 'repeat' ? '' : 'd-none d-print-block']">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold mb-0 text-dark">4. Top Issues with Repeat Student Contacts</h6>
                <small class="text-muted">Identifies categories and subcategories driving student re-contacts on unresolved matters</small>
              </div>
              <vue-good-table
                :columns="repeatColumns"
                :rows="repeatIssues"
                :line-numbers="true"
                :search-options="{ enabled: true, placeholder: 'Search issue or subcategory...' }"
                :pagination-options="{ enabled: true, perPage: 10 }"
              >
                <template slot="table-row" slot-scope="props">
                  <span v-if="props.column.field === 'repeatContactRate'">
                    <span :class="['badge px-2 py-1', props.row.repeatContactRate <= 15 ? 'badge-success' : (props.row.repeatContactRate <= 30 ? 'badge-warning' : 'badge-danger')]" style="font-size: 12px;">
                      {{ props.row.repeatContactRate }}%
                    </span>
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Chart } from 'highcharts-vue'
import ISO8601Date from '../../../helper/ISO8601Date'
import RangedatePicker from 'vue-rangedate-picker'

export default {
  name: 'ComplianceReports',
  components: {
    highcharts: Chart,
    RangedatePicker
  },
  data() {
    return {
      currentPeriod: 'month',
      periodLabel: 'This Month',
      startDate: '',
      endDate: '',
      ackSlaHours: 24,
      resSlaHours: 48,
      activeTab: 'agents',
      downloadLink: '',

      // Report Summaries
      agentResSummary: {
        totalEnquiries: 0,
        totalClosed: 0,
        firstContactResolved: 0,
        escalatedEnquiries: 0,
        resolutionRate: 0,
        overallFcrRate: 0
      },
      agentBreakdown: [],

      ackSummary: {
        totalTickets: 0,
        totalAcknowledged: 0,
        compliantCount: 0,
        breachedCount: 0,
        pendingAck: 0,
        complianceRate: 0,
        avgAckHours: 0,
        standardHours: 24
      },
      ackCategoryBreakdown: [],
      ackChannelBreakdown: [],

      resSummary: {
        totalResolved: 0,
        compliantResolved: 0,
        breachedResolved: 0,
        overdueUnresolved: 0,
        complianceRate: 0,
        avgResolutionHours: 0,
        standardHours: 48
      },
      resCategoryBreakdown: [],

      repeatSummary: {
        totalInquiringStudents: 0,
        repeatStudents: 0,
        singleContactStudents: 0,
        repeatContactRate: 0,
        totalReopenedTickets: 0
      },
      repeatIssues: [],
      trends: [],

      // Table columns
      agentColumns: [
        { label: 'Agent Name', field: 'agentName' },
        { label: 'Email', field: 'agentEmail' },
        { label: 'Total Assigned', field: 'totalAssigned', type: 'number' },
        { label: 'Resolved Directly', field: 'resolvedDirectly', type: 'number' },
        { label: 'Escalated', field: 'escalated', type: 'number' },
        { label: 'Pending', field: 'pending', type: 'number' },
        { label: 'Resolution Rate', field: 'resolutionRate', type: 'number' }
      ],
      ackCatColumns: [
        { label: 'Category', field: 'categoryName' },
        { label: 'Total Tickets', field: 'totalTickets', type: 'number' },
        { label: 'Acknowledged', field: 'acknowledged', type: 'number' },
        { label: 'Compliant (<= Standard)', field: 'compliantCount', type: 'number' },
        { label: 'Breached (> Standard)', field: 'breachedCount', type: 'number' },
        { label: 'Avg Time (Hrs)', field: 'avgAckHours', type: 'number' },
        { label: 'Compliance Rate', field: 'complianceRate', type: 'number' }
      ],
      resCatColumns: [
        { label: 'Category', field: 'categoryName' },
        { label: 'Target (Hrs)', field: 'targetHours', type: 'number' },
        { label: 'Total Resolved', field: 'totalResolved', type: 'number' },
        { label: 'Compliant', field: 'compliantCount', type: 'number' },
        { label: 'Breached', field: 'breachedCount', type: 'number' },
        { label: 'Overdue Open', field: 'overdueCount', type: 'number' },
        { label: 'Avg Res Time (Hrs)', field: 'avgResolutionHours', type: 'number' },
        { label: 'Compliance Rate', field: 'complianceRate', type: 'number' }
      ],
      repeatColumns: [
        { label: 'Category', field: 'categoryName' },
        { label: 'Subcategory / Issue Topic', field: 'subCategoryName' },
        { label: 'Total Enquiries', field: 'totalEnquiries', type: 'number' },
        { label: 'Repeat Inquiring Students', field: 'repeatStudents', type: 'number' },
        { label: 'Reopened Count', field: 'reopenedTickets', type: 'number' },
        { label: 'Repeat Contact Rate', field: 'repeatContactRate', type: 'number' }
      ]
    }
  },

  computed: {
    authUserRole() {
      return this.$store.state.authUser ? this.$store.state.authUser.Role : 'admin'
    },

    trendsChartOptions() {
      let categories = []
      let agentRates = []
      let ackRates = []
      let resRates = []
      let repeatRates = []

      for (let i = 0; i < this.trends.length; i++) {
        categories.push(this.trends[i].label)
        agentRates.push(this.trends[i].agentResolutionRate)
        ackRates.push(this.trends[i].ackComplianceRate)
        resRates.push(this.trends[i].resComplianceRate)
        repeatRates.push(this.trends[i].repeatContactRate)
      }

      return {
        chart: { type: 'spline' },
        title: { text: '' },
        xAxis: { categories: categories, crosshair: true },
        yAxis: {
          title: { text: 'Percentage (%)' },
          max: 100,
          min: 0,
          labels: { format: '{value}%' }
        },
        tooltip: { shared: true, valueSuffix: '%' },
        credits: { enabled: false },
        series: [
          { name: 'Agent Resolution Rate', data: agentRates, color: '#28a745' },
          { name: 'Compliance Rate', data: ackRates, color: '#17a2b8' },
          { name: 'Resolution Compliance Rate', data: resRates, color: '#007bff' },
          { name: 'Repeat Contact Rate', data: repeatRates, color: '#ffc107', dashStyle: 'ShortDash' }
        ]
      }
    },

    channelChartOptions() {
      let categories = []
      let compliant = []
      let breached = []

      for (let i = 0; i < this.ackChannelBreakdown.length; i++) {
        categories.push(this.ackChannelBreakdown[i].channel)
        compliant.push(this.ackChannelBreakdown[i].compliantCount)
        breached.push(this.ackChannelBreakdown[i].breachedCount)
      }

      return {
        chart: { type: 'bar' },
        title: { text: '' },
        xAxis: { categories: categories },
        yAxis: {
          min: 0,
          title: { text: 'Total Tickets' }
        },
        legend: { reversed: true },
        plotOptions: {
          series: { stacking: 'normal' }
        },
        credits: { enabled: false },
        series: [
          { name: 'Compliant ', data: compliant, color: '#17a2b8' },
          { name: 'Breached ', data: breached, color: '#dc3545' }
        ]
      }
    }
  },

  mounted() {
    this.fetchReports('month')
  },

  methods: {
    onPeriodChange(val) {
      if (val !== 'range') {
        this.fetchReports(val)
      }
    },

    onDateRangeSelected(data) {
      this.startDate = ISO8601Date(data.start)
      this.endDate = ISO8601Date(data.end)
      this.fetchReports('range')
    },

    fetchReports(period) {
      let loader = this.$loading.show()
      let url = `/admin/compliance-reports?q=${period}&ack_sla=${this.ackSlaHours}&res_sla=${this.resSlaHours}`
      if (period === 'range' && this.startDate && this.endDate) {
        url += `&start=${this.startDate}&end=${this.endDate}`
      }

      axios
        .get(url)
        .then(res => {
          loader.hide()
          if (res.data && res.data.status === 'success') {
            this.periodLabel = res.data.periodLabel
            this.agentResSummary = res.data.agentResolution.summary || {}
            this.agentBreakdown = res.data.agentResolution.breakdown || []

            this.ackSummary = res.data.acknowledgementCompliance.summary || {}
            this.ackCategoryBreakdown = res.data.acknowledgementCompliance.categoryBreakdown || []
            this.ackChannelBreakdown = res.data.acknowledgementCompliance.channelBreakdown || []

            this.resSummary = res.data.resolutionCompliance.summary || {}
            this.resCategoryBreakdown = res.data.resolutionCompliance.categoryBreakdown || []

            this.repeatSummary = res.data.repeatContact.summary || {}
            this.repeatIssues = res.data.repeatContact.issues || []

            this.trends = res.data.trends || []
            this.downloadLink = res.data.downloadLink || ''
          }
        })
        .catch(err => {
          loader.hide()
          console.error('Failed to load compliance reports', err)
          this.$swal({
            title: 'Error',
            text: 'Unable to load compliance reports. Please try again.',
            icon: 'error'
          })
        })
    },

    exportCSV() {
      let downloadUrl = this.downloadLink
      if (!downloadUrl) {
        let baseUrl = process.env.VUE_APP_API_URL || ''
        downloadUrl = `${baseUrl}/download/compliance-reports?q=${this.currentPeriod}&ack_sla=${this.ackSlaHours}&res_sla=${this.resSlaHours}`
        if (this.currentPeriod === 'range' && this.startDate && this.endDate) {
          downloadUrl += `&start=${this.startDate}&end=${this.endDate}`
        }
      }
      window.open(downloadUrl, '_blank')
    },

    exportPDF() {
      this.$nextTick(() => {
        window.print()
      })
    }
  }
}
</script>

<style scoped>
.pageTitle {
  font-size: 24px;
  font-weight: 700;
  color: #212529;
}

.filter-card {
  background-color: #ffffff;
  border-radius: 8px;
}

.kpi-card {
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
}

.kpi-title {
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 30px;
  line-height: 1.2;
}

.kpi-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon {
  font-size: 22px;
}

.bg-soft-success {
  background-color: rgba(40, 167, 69, 0.12);
}

.bg-soft-info {
  background-color: rgba(23, 162, 184, 0.12);
}

.bg-soft-primary {
  background-color: rgba(0, 123, 255, 0.12);
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.15);
}

.report-tabs >>> .nav-link {
  font-weight: 600;
  color: #6c757d;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 10px 18px;
  font-size: 14px;
}

.report-tabs >>> .nav-link.active {
  color: #0061f2;
  border-bottom: 2px solid #0061f2;
  background-color: transparent;
}

.chart-card-header,
.card-header.bg-white {
  color: #212529 !important;
  background-color: #ffffff !important;
  border-bottom: 1px solid #e3e6f0 !important;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.chart-card-header svg {
  font-size: 1.25rem !important;
  margin-right: 0.5rem !important;
}

@media print {
  /* Hide interactive controls, buttons, sidebars and navigation in PDF export */
  .d-print-none,
  .sidebar,
  .sidebar-button,
  .home-content,
  .filter-card,
  .report-tabs,
  .btn,
  .vgt-global-search,
  .vgt-pagination,
  .card-footer,
  .arrow {
    display: none !important;
  }

  /* Expand body and content to 100% page width with white background */
  body,
  .home-section,
  .container-fluid {
    width: 100% !important;
    left: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }

  /* Display all report sections sequentially in PDF export */
  .tab-report-section {
    display: block !important;
    page-break-inside: avoid;
    margin-top: 24px !important;
  }

  /* Clean KPI card borders for print */
  .kpi-card {
    border: 1px solid #dee2e6 !important;
    box-shadow: none !important;
    page-break-inside: avoid;
    margin-bottom: 12px !important;
  }

  /* Highcharts chart container width in print */
  .highcharts-container {
    width: 100% !important;
  }

  /* Compact table font for print */
  .vgt-table {
    font-size: 11px !important;
  }
}
</style>
